import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import { useRegisterPlanImageMutation } from 'api/deviceApi';
import { DeviceInfoRequest, PlanImageRequest } from 'api/DeviceRequest';
import ErrorMessage from 'components/widget/ErrorMessage';
import Device from 'model/DeviceInfo';
import { DEVICE_MODAL_TYPE, YN_TYPE } from 'types/enumType';
import { convertDeviceTypeToString } from 'util/commonUtil';

import PlanImageModal from './PlanImageModal';
import { deviceInfoSchema } from './resolver/deviceResolvers';

type DeviceModalProps = {
  type: DEVICE_MODAL_TYPE;
  device: Device;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: DeviceInfoRequest) => void;
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 0,
  },
};
export default function DeviceModal({
  type,
  device,
  isOpen,
  onClose,
  onSubmit,
}: DeviceModalProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DeviceInfoRequest>({
    resolver: deviceInfoSchema,
  });
  const [isImgModalOpen, setIsImgModalOpen] = useState(false);
  const handleOpenImageModal = () => {
    setIsImgModalOpen(true);
  };
  const handleCloseImageModal = () => {
    setIsImgModalOpen(false);
  };

  const uploadPlanImageMutate = useRegisterPlanImageMutation();
  const uploadPlanImage = (req: PlanImageRequest) => {
    uploadPlanImageMutate.mutate(
      { ...req, resetPos: YN_TYPE.Y },
      {
        onSuccess: ({ data: resp }) => {
          if (resp.code === 200) {
            alert(t('validation.saveImage'));
            setIsImgModalOpen(false);
            onClose();
          } else {
            alert(resp.message);
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!device) {
      return;
    }

    if (!isOpen) {
      reset();
      return;
    }

    reset({ ...device });
  }, [isOpen, device, reset]);

  return (
    <>
      <ReactModal isOpen={isOpen} style={customStyles} onRequestClose={onClose}>
        <div id={'popup-wrap'}>
          <div className={'popup-wrap device-modal'} id={'etc-modal'}>
            <div className={'popup-head'}>
              <p className={'poptit'}>{convertDeviceTypeToString(type)}</p>
            </div>
            <div className={'popup-cont'}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className={'cont'}>
                  <div className={'filter-wrap'}>
                    <div className={'filter'}>
                      <dl>
                        <dt>{`${convertDeviceTypeToString(type)} ${t(
                          'user.name',
                        )}`}</dt>
                        <dd>
                          <input
                            type={'text'}
                            defaultValue={device.name}
                            {...register('name')}
                          />
                          <input
                            type={'hidden'}
                            defaultValue={device.id}
                            {...register('id')}
                          />
                          <input
                            type={'hidden'}
                            defaultValue={type}
                            {...register('type')}
                          />
                        </dd>
                      </dl>
                      {device.id > 0 && type === DEVICE_MODAL_TYPE.SECTION && (
                        <dl>
                          <dt>{t('device.image')}</dt>
                          <dd>
                            <button
                              type={'button'}
                              className={'orange_white_btn'}
                              onClick={handleOpenImageModal}
                            >
                              {t('device.imageRegist')}
                            </button>
                          </dd>
                        </dl>
                      )}
                    </div>
                  </div>
                  <ErrorMessage errors={errors} />
                  <div className={'btn-wrap'}>
                    <button type={'submit'} className={'orange_white_btn'}>
                      {t('button.save')}
                    </button>
                    <button
                      type={'button'}
                      className={'gray_white_btn'}
                      onClick={onClose}
                    >
                      {t('button.close')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ReactModal>
      <PlanImageModal
        type={DEVICE_MODAL_TYPE.SECTION}
        id={device.id}
        isOpen={isImgModalOpen}
        isLoading={uploadPlanImageMutate.isLoading}
        onClose={handleCloseImageModal}
        onSubmit={uploadPlanImage}
      />
    </>
  );
}
